import { Fragment, useCallback, useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { styled } from '@mui/material'
import { useTranslation } from 'src/utils/i18n'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import { getLocalAndroidAll } from 'src/@api/dashboard'
import { androidLocalRefresh } from 'src/@api/android'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'
import ContentLoading from 'src/components/ContentLoading'

dayjs.extend(duration)

// 自定义隐藏滚动条且保留滚动特性(div
const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

// 自定义隐藏滚动条且保留滚动特性(ol
const ScrollOl = styled('ol')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'none',  /* 适用于 Firefox */
  '&::-webkit-scrollbar': {
    display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
  }
})

const ScreenTime = () => {
  const { t, locale } = useTranslation('common')
  const dispatch = useDispatch()
  const [showIndex, setShowIndex] = useState(-1) // 悬浮圆柱体时显示的下标
  const [totalWidth, setTotalWidth] = useState(0) // 柱状体显示区域总宽度
  const [totalHeight, setTotalHeight] = useState(0) // 柱状体显示区域总高度
  const [highestTime, setHighestTime] = useState(0) //最高应用使用时长
  const [smallWidth, setSmallWidth] = useState(0) // 单个圆柱体宽度
  const [smallWidth2, setSmallWidth2] = useState(0) // 单个圆柱体宽度(不包含边距)
  const [spanToRight, setSpanToRight] = useState(true) // 悬浮内容方向
  const [spanToTop, setSpanToTop] = useState(0) // 悬浮内容距顶部距离
  const [clickIndex, setClickIndex] = useState({ index: -1, state: 0 }) // 移动端柱状条点击状态
  const contentRef = useRef(null) // 柱状容器
  const showIndexRef = useRef(showIndex) // 下标存储
  const [innerWidth, setInnerWidth] = useState(0) // 屏幕实时宽度
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据
  const [allData, setAllData] = useState([])  //所有数据
  const [showData, setShowData] = useState([])  //展示数据
  const [totalSeconds, setTotalSeconds] = useState(0) // 所有app的使用时长秒数
  const [averageSeconds, setAverageSeconds] = useState(0) // app平均使用时间
  const [allApp, setAllApp] = useState([]) // 全部安装的app

  const loadScreenTimeData = useCallback(async (deviceUdid, { signal, isCurrent } = {}) => {
    const requestOptions = signal ? { signal } : undefined

    try {
      const [usageResponse, appResponse] = await Promise.all([
        getLocalAndroidAll(
          {
            username: readUserEmail(),
            params: {
              category: 'APP_USAGE',
              offset: 0,
              limit: 200,
              deviceUdid
            }
          },
          requestOptions
        ),
        getLocalAndroidAll(
          {
            username: readUserEmail(),
            params: {
              category: 'INSTALLED_APP',
              offset: 0,
              limit: 200,
              deviceUdid
            }
          },
          requestOptions
        ),
        androidLocalRefresh({ device_id: deviceUdid, type: '43' }).catch(console.error)
      ])

      if (isCurrent && !isCurrent()) return

      setAllData([...(usageResponse.data.contents?.data || [])])
      setAllApp([...(appResponse.data?.contents || [])])
    } catch (e) {
      if (isCurrent && !isCurrent() || e?.code === 'ERR_CANCELED') return
      console.error(e)
    } finally {
      if (!isCurrent || isCurrent()) setIsLoading(false)
    }
  }, [])

  // 设备切换时重置屏幕使用时间并重新拉数
  useDeviceScopedInit(
    useCallback(
      (_, { context, signal, isCurrent }) => {
        const deviceUdid = readLocalDeviceUdid(context)
        if (!deviceUdid) return

        setIsLoading(true)
        setAllData([])
        setShowData([])
        setAllApp([])
        setTotalSeconds(0)
        setAverageSeconds(0)
        setHighestTime(0)
        setShowIndex(-1)
        setClickIndex({ index: -1, state: 0 })
        loadScreenTimeData(deviceUdid, { signal, isCurrent })
      },
      [loadScreenTimeData]
    )
  )

  useEffect(() => {
    if (!allData.length) return

    const maxSeconds = Math.max(...allData.map(item => item.seconds || 0))

    const newData = allData
      .map(item => {
        const app = allApp.find(a => a.name === item.className)

        return {
          ...item,
          iconUrl: app ? app.iconUrl : '',
          percent: maxSeconds ? ((item.seconds / maxSeconds) * 100).toFixed(2) : 0
        }
      })
      .sort((a, b) => b.seconds - a.seconds)

    setHighestTime(maxSeconds)
    setShowData(newData)
  }, [allData, allApp])

  // 获取平均使用时间
  useEffect(() => {
    let total = 0
    let sum = 0
    allData.forEach(item => {
      total += item.seconds
      sum++
    })

    setTotalSeconds(total)
    setAverageSeconds(total / sum)
  }, [allData])

  // 初始化每个柱状体的宽度
  useEffect(() => {
    let totalWidth = 0
    if (contentRef.current) {
      totalWidth = contentRef.current.offsetWidth

      setTotalWidth(totalWidth)
      setTotalHeight(contentRef.current.clientHeight)
      setSmallWidth(totalWidth / allData.length)
      setSmallWidth2(totalWidth / allData.length - 10)
    }
  }, [innerWidth])

  // 监听鼠标位置
  useEffect(() => {
    const handleMouseY = event => {
      if (contentRef.current && showIndexRef.current !== -1) {
        const container = contentRef.current

        // 获取容器的位置信息
        const rect = container.getBoundingClientRect()

        // 计算鼠标距离容器顶部的高度
        const mouseHeight = event.clientY - rect.top

        // 确保鼠标高度在容器范围内
        const boundedHeight = Math.max(0, Math.min(mouseHeight, rect.height))

        setSpanToTop(boundedHeight)
      }
    }

    if (contentRef.current) contentRef.current.addEventListener('mousemove', handleMouseY)

    return () => {
      if (contentRef.current) contentRef.current.removeEventListener('mousemove', handleMouseY)
    }
  }, [])

  // 计算柱状条高度
  const calculateSmallHeight = (item, type) => {
    let rate = 0
    if (type === 0) {
      rate += item
    } else rate = item

    return totalHeight * rate / 100
  }

  // 悬浮式修改各状态
  const changeSpanMouseOver = (index, type) => {
    if (type === 0) {
      const nowWidth = smallWidth * (index + 1)
      if (nowWidth + 213 > totalWidth && innerWidth > 990) setSpanToRight(false)
      else if ((46 * (index + 1) + 150) > totalWidth && innerWidth < 990) setSpanToRight(false)
      setShowIndex(index)
      showIndexRef.current = index
    } else {
      setSpanToRight(true)
      setShowIndex(-1)
      showIndexRef.current = -1
    }
  }

  const convertToEvenMinutes = (seconds) => {
    let minutes = Math.floor(seconds / 60)

    if (minutes < 1) return 1

    // 往下取偶数
    if (minutes % 2 !== 0) {
      minutes = minutes - 1
    }

    return minutes < 1 ? 1 : minutes
  }

  // 监听屏幕宽度
  useEffect(() => {
    const handleWidth = () => {
      setInnerWidth(window.innerWidth)
    }
    handleWidth()
    window.addEventListener('resize', handleWidth)

    return () => {
      window.removeEventListener('resize', handleWidth)
    }
  }, [])

  // 获取当前语种的dayjs
  useEffect(() => {
    const handleLocale = async () => {
      switch (locale) {
        case 'fr':
          await import('dayjs/locale/fr')
          dayjs.locale('fr')
          break
        case 'jp':
          await import('dayjs/locale/ja')
          dayjs.locale('ja')
          break
        case 'es':
          await import('dayjs/locale/es')
          dayjs.locale('es')
          break
        case 'de':
          await import('dayjs/locale/de')
          dayjs.locale('de')
          break
        case 'it-ch':
          await import('dayjs/locale/it')
          dayjs.locale('it')
          break
        case 'pt':
          await import('dayjs/locale/pt')
          dayjs.locale('pt')
          break
        case 'kr':
          await import('dayjs/locale/ko')
          dayjs.locale('ko')
          break
        case 'ar':
          await import('dayjs/locale/ar')
          dayjs.locale('ar')
          break
        case 'tr':
          await import('dayjs/locale/tr')
          dayjs.locale('tr')
          break
        default:
          await import('dayjs/locale/en')
          dayjs.locale('en')
      }
    }

    handleLocale()
  }, [locale])

  useEffect(() => {
    const title = t('Screen Time')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  return (
    <Fragment>
      {/* 展示页 */}
      <ScrollDiv
        className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] overflow-x-hidden overflow-y-auto flex smj:flex-col relative'>
        <h2 className={`w-full text-left text-[18px] text-[#000000] font-[600] leading-[27px] mt-[42px] smj:mt-5 mb-[6px] ml-[20px] smjs:hidden smj:order-2 ${allData.length == 0 && 'hidden'}`}>{t('App usage')}</h2>

        {/* 左侧app使用时长列表 */}
        <ScrollOl className='w-full h-auto smjs:!w-[327px] smjs:[border-right:1px_solid_#E7E8EC] smjs:overflow-auto py-[10px] smj:order-3'>
          {
            showData.map((item, index) => (
              <li key={index} className='h-[60px] flex items-center p-[9px_9px_14px_12px] smj:p-[10px_14px_14px] mx-[3px] smj:mx-[6px] hover:bg-[#F4F5F7] hover:rounded-[6px]'>
                <Image src={'https://cos.spyx.com/' + item.iconUrl} alt={item.className} width={32} height={32} className='w-[32px] h-[32px] mr-3 rounded-[50%]' />

                <div className='w-full flex flex-col justify-center'>
                  <div className='h-[21px] flex justify-between items-center'>
                    <p className='text-[14px] text-[#4D5976] font-[500] leading-[21ps]'>{item.className}</p>
                    <span className='text-[12px] text-[#80889D] font-[400] leading-[18px]'>
                      {`${dayjs.duration(item.seconds, 'seconds').hours() > 0 ? (
                        dayjs.duration(item.seconds, 'seconds').hours() < 10 ? '0' + dayjs.duration(item.seconds, 'seconds').hours() + `h ` : dayjs.duration(item.seconds, 'seconds').hours() + `h `
                      ) : ''}
                      ${dayjs.duration(item.seconds, 'seconds').minutes() < 10 ? '0' + dayjs.duration(item.seconds, 'seconds').minutes() : dayjs.duration(item.seconds, 'seconds').minutes()}m
                      ${dayjs.duration(item.seconds, 'seconds').seconds() < 10 ? '0' + dayjs.duration(item.seconds, 'seconds').seconds() : dayjs.duration(item.seconds, 'seconds').seconds()}s`}
                    </span>
                  </div>

                  <div className='w-full h-[2px] bg-[#EDEEF0] mt-2 mb-1 overflow-hidden'>
                    <div className='h-[2px] bg-[#1A90FF]' style={{ width: `${item.seconds / totalSeconds * 100}%`, minWidth: `${item.seconds > 0 ? '2' : '0'}%` }}></div>
                  </div>
                </div>
              </li>
            ))
          }
        </ScrollOl>

        {/* 右侧柱状体&平均数据展示 */}
        <div className='smj:w-full smjs:w-[calc(100%-327px)] pt-[30px] smj:pt-[14px] px-[90px] smj:px-[12px] smj:order-1 smjs:overflow-hidden'>

          <h2 className='w-full text-center text-[18px] text-[#000000] font-[600] leading-[27px] smjs:hidden'>{t('Screen Time')}</h2>

          {/* 平均使用时长（无数据时不显示，只留中间的空状态提示） */}
          {allData.length > 0 && (
          <div className='w-auto h-max smj:mt-[20px] smj:pl-[8px]'>
            <h2 className='text-[18px] text-[#334062] font-[700] leading-[27px] smjs:mb-2'>{t('Todays')}</h2>
            <p className='text-[14px] smj:text-[16] smj:leading-[24px] text-[#666666] font-[700]'>{t('Average App Screen time')}</p>
            <div className='smjs:h-[130px] smj:mt-[14px] smj:mb-[25px] flex flex-wrap items-center'>
              <time className='text-[40px] smj:text-[24px] smj:leading-[36px] text-[#334062] font-[700] mr-4'>{
                `${dayjs.duration(averageSeconds, 'seconds').hours() > 0 ? (
                  dayjs.duration(averageSeconds, 'seconds').hours() < 10 ? '0' + dayjs.duration(averageSeconds, 'seconds').hours() + `h ` : dayjs.duration(averageSeconds, 'seconds').hours() + `h `
                ) : ''}
                ${dayjs.duration(averageSeconds, 'seconds').minutes() < 10 ? '0' + dayjs.duration(averageSeconds, 'seconds').minutes() : dayjs.duration(averageSeconds, 'seconds').minutes()}m
                ${dayjs.duration(averageSeconds, 'seconds').seconds() < 10 ? '0' + dayjs.duration(averageSeconds, 'seconds').seconds() : dayjs.duration(averageSeconds, 'seconds').seconds()}s`
              }</time>
            </div>
          </div>
          )}
          {/* 柱状图区（无数据时不显示，只留中间的空状态提示） */}
          {allData.length > 0 && (
          <div className='flex'>
            {/* 左侧一小时时间列表 */}
            <ol className='w-8 h-full grid grid-cols-1 grid-rows-[repeat(3,1fr)] gap-[233px] smj:gap-[87px] items-start text-[12px] smj:text-[10px] text-[#A5A5A5] font-[400] leading-[18px] smj:leading-[15px] mr-3 smj:mr-[5px]'>
              <li className='w-full text-right text-nowrap'>{convertToEvenMinutes(highestTime)}m</li>
              <li className='w-full text-right text-nowrap'>{convertToEvenMinutes(highestTime) / 2}m</li>
              <li className='w-full text-right text-nowrap'>0</li>
            </ol>

            {/* 柱状体 */}
            <ScrollDiv className='w-[calc(100%-32px)] smj:overflow-x-auto smj:h-[245px]'>
              <div className='w-full smj:w-auto h-[515px] smj:h-[215px] flex'>

                {/* 柱状体显示部分 */}
                <div ref={contentRef} className='w-full h-full [border-left:2px_dashed_#E8E7EC] smj:[border-left:1px_dashed_#E8E7EC] [border-bottom:2px_dashed_#E8E7EC] smj:[border-bottom:1px_dashed_#E8E7EC] relative pl-[6px]'>

                  {/* 遮罩层,用于移动端点击空白地方收起悬浮窗 */}
                  <div className={`w-full h-full absolute z-[54] ${clickIndex.state === 0 ? 'hidden' : ''}`} onClick={() => {
                    changeSpanMouseOver(clickIndex.index, clickIndex.state)
                    setClickIndex(old => ({ ...old, state: 0 }))
                  }}>

                  </div>

                  {/* 所有柱状体 */}
                  <ol className='w-full h-full flex items-end'>
                    {
                      showData.map((item, index) => (
                        <li
                          key={index}
                          className='w-full flex flex-col justify-end relative max-w-[60px] smj:!w-[36px] smj:flex-shrink-0'
                          style={{ width: `${smallWidth}px`, height: '100%' }}
                        >
                          <div className={`bg-[#F1E600] transition-[height] duration-300 ease-linear cursor-pointer z-[56] smj:!w-[30px]`}
                            style={{ width: `calc(100% - 10px)`, height: `${item.percent}%` }}
                            onMouseOver={() => innerWidth > 990 && changeSpanMouseOver(index, 0)}
                            onMouseOut={() => innerWidth > 990 && changeSpanMouseOver(index, 1)}
                            onClick={() => {
                              if (innerWidth <= 990) {
                                if (clickIndex.index === index) {
                                  changeSpanMouseOver(index, clickIndex.state)
                                  setClickIndex(old => ({ ...old, state: old.state === 0 ? 1 : 0 }))
                                } else {
                                  changeSpanMouseOver(index, 0)
                                  setClickIndex(old => ({ index, state: 1 }))
                                }
                              }
                            }}
                          ></div>

                          {/* 详细使用时间悬浮 */}
                          <span
                            className={`w-[max-content] text-nowrap min-h-[30px] p-3 bg-[#F5F6FA] rounded-[6px] absolute [box-shadow:0px_0px_12px_0px_#00000021] ${showIndex !== index ? 'hidden' : ''} z-[58]`}
                            style={{
                              translate: `${spanToRight ? smallWidth + 'px' : '-100%'}`,
                              top: `${innerWidth > 990 ? `${spanToTop - (totalHeight - calculateSmallHeight(item, 0)) - 21}px` : `unset`}`
                            }}
                          >
                            <div className='flex items-center mb-2'>
                              <Image src={'https://cos.spyx.com/' + item.iconUrl} width={20} height={20} alt='' className='mr-2' />
                              {item.className}
                            </div>
                            {t('Total time')}:{' '}
                            {`${dayjs.duration(item.seconds, 'seconds').hours() > 0 ? (dayjs.duration(item.seconds, 'seconds').hours() < 10 ? '0' +
                              dayjs.duration(item.seconds, 'seconds').hours() + `h ` : dayjs.duration(item.seconds, 'seconds').hours() + `h `) : ''}
                                ${dayjs.duration(item.seconds, 'seconds').minutes() < 10 ? '0' + dayjs.duration(item.seconds, 'seconds').minutes() : dayjs.duration(item.seconds, 'seconds').minutes()}m
                                ${dayjs.duration(item.seconds, 'seconds').seconds() < 10 ? '0' + dayjs.duration(item.seconds, 'seconds').seconds() : dayjs.duration(item.seconds, 'seconds').seconds()}s`}

                            <ul className='grid grid-cols-1 gap-x-[30px] gap-y-[4px] mt-[8px] text-[12px] text-[#000000] font-[Poppins] font-[400] leading-[18px] select-none'>
                              <li className={`flex items-center cursor-pointer`}>
                                {t('First open time')}: {dayjs(item.firstOpen).format('HH:mm:ss')}
                              </li>
                              <li className={`flex items-center cursor-pointer`}>
                                {t('Last close time')}: {dayjs(item.lastClose).format('HH:mm:ss')}
                              </li>
                            </ul>
                          </span>

                          <span className={`w-full absolute -bottom-[26px] right-[5px] smj:right-[3px] flex justify-center z-100`}>
                            <Image src={'https://cos.spyx.com/' + item.iconUrl} width={20} height={20} alt='' className={`w-[${smallWidth2 * 0.4}px] h-[${smallWidth2 * 0.4}px] smj:w-[20px] smj:h-[20px]`} />
                          </span>
                        </li>
                      ))
                    }
                  </ol>
                </div>
              </div>
            </ScrollDiv>
          </div>
          )}
        </div>
        {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(allData.length > 0 || isLoading) ||
          <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
            {t('It appears theres no data to show here yet')}<br />
            {t('If data exists it may take up to 1 hour to download and appear')}<br />
            {t('We suggest checking back in a little while')}
          </div>}
      </ScrollDiv>
    </Fragment >
  )
}

export default ScreenTime